import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faFileContract,
  faRotate,
  faSearch,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { platformSubscriptionService } from '@/services/platform-subscription.service';
import type { SubscriptionListItem, SubscriptionStatus } from '@/types/platform-subscription.types';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import {
  SubscriptionStatusBadge,
  formatExpiry,
  formatPeriod,
  formatSubscriptionDate,
} from './subscription-presentation';

/**
 * Phase 12.4E — the Subscriptions list.
 *
 * Design of record: `SUBSCRIPTIONS_DESIGN.md` v1.4 §5.1. Authorized by **D-109 §1**.
 *
 * ── One row per company ─────────────────────────────────────────────────────
 * Each row is a company's CURRENT subscription (OD-1: latest `startedAt`). Prior
 * rows are not listed here — they belong to the History card on the detail page.
 *
 * ── Everything that narrows the list is server-side ─────────────────────────
 * Search, status, page size and offset all go to the API. Filtering a single
 * page in the browser produces a search box that silently lies: "acme" finds
 * nothing unless Acme happens to be in the first twenty rows.
 *
 * ── D-17: that state lives in the URL ───────────────────────────────────────
 * A filtered view is shareable, survives a reload and works with the browser's
 * back button. This is the first Platform Console list to do it — Companies
 * keeps the equivalent state in `useState` — so the mechanism is written out
 * here rather than copied.
 *
 * ── No Plan filter, by decision ─────────────────────────────────────────────
 * **D-109 §4** omits it: every subscription is on one plan today, and the only
 * plan endpoint returns prices and capabilities that this console is forbidden
 * to display. Plan **identity** still appears, as the neutral `plan.slug`.
 */

const PAGE_SIZES = [20, 50, 100] as const;
const DEFAULT_LIMIT = PAGE_SIZES[0];

/**
 * The five governed statuses. **`PAST_DUE` is deliberately absent** — 12.4B
 * reconciled it away (G-7), and the API rejects it with a 400.
 */
const STATUS_FILTERS: { value: '' | SubscriptionStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'TRIAL', label: 'Trial' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

const VALID_STATUSES = new Set(STATUS_FILTERS.map((f) => f.value).filter(Boolean));

/** §5.1 loading state — a skeleton table, not a spinner that hides the shape. */
function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {Array.from({ length: 7 }).map((__, j) => (
            <td key={j} className="px-4 py-3.5">
              <div className="h-3 rounded bg-muted" style={{ width: j === 0 ? '70%' : '45%' }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function SubscriptionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL is the source of truth for filter state (D-17) ────────────────────
  // Read it on every render rather than mirroring it into state: a back-button
  // press changes the URL, and mirrored state would quietly ignore it.
  const rawStatus = searchParams.get('status') ?? '';
  const status: '' | SubscriptionStatus = VALID_STATUSES.has(rawStatus as SubscriptionStatus)
    ? (rawStatus as SubscriptionStatus)
    : '';
  const search = searchParams.get('search')?.trim() ?? '';

  const parsedLimit = Number(searchParams.get('limit'));
  const limit = (PAGE_SIZES as readonly number[]).includes(parsedLimit)
    ? parsedLimit
    : DEFAULT_LIMIT;

  const parsedOffset = Number(searchParams.get('offset'));
  const offset = Number.isInteger(parsedOffset) && parsedOffset > 0 ? parsedOffset : 0;

  const [subscriptions, setSubscriptions] = useState<SubscriptionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // The search box is local so typing stays responsive; the URL is updated on a
  // debounce. Seeded from the URL so a shared link shows its own search term.
  const [searchInput, setSearchInput] = useState(search);

  /**
   * Write filter state to the URL.
   *
   * Empty values are REMOVED rather than written as `?search=`, so a cleared
   * filter produces a clean shareable URL. Any change that alters the result set
   * resets `offset` — staying on page 4 of a new search shows a blank page.
   */
  const updateParams = useCallback(
    (changes: Record<string, string | number | null>, { resetOffset = true } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '' || value === undefined) next.delete(key);
            else next.set(key, String(value));
          }
          if (resetOffset && !('offset' in changes)) next.delete('offset');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Debounced search → URL. One request per pause in typing, not per keystroke.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === search) return undefined;
    const timer = setTimeout(() => updateParams({ search: trimmed || null }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, search, updateParams]);

  // Guards against a slow earlier request overwriting a newer one's results.
  const requestId = useRef(0);

  useEffect(() => {
    const current = ++requestId.current;
    setLoading(true);
    setError('');

    platformSubscriptionService
      .list({
        limit,
        offset,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      })
      .then((page) => {
        if (current !== requestId.current) return;
        setSubscriptions(page.items);
        setTotal(page.total);
      })
      .catch(() => {
        if (current !== requestId.current) return;
        // An error is never rendered as an empty table — the 12.2B honesty rule.
        setSubscriptions([]);
        setError('Could not load subscriptions. Check your connection and try again.');
      })
      .finally(() => {
        if (current === requestId.current) setLoading(false);
      });
  }, [limit, offset, search, status, reloadToken]);

  const hasFilters = search !== '' || status !== '';

  const clearFilters = useCallback(() => {
    setSearchInput('');
    updateParams({ search: null, status: null });
  }, [updateParams]);

  const openSubscription = useCallback(
    (subscriptionId: string) => {
      // Interpolating an absent id yields the literal "undefined" in the URL,
      // which routes but then fails at the API — the 12.2A guard, retained.
      if (!subscriptionId) return;
      navigate(PLATFORM_ROUTES.SUBSCRIPTION_DETAIL.replace(':id', subscriptionId));
    },
    [navigate],
  );

  const rangeLabel = useMemo(() => {
    if (total === 0) return 'No subscriptions';
    const first = offset + 1;
    const last = Math.min(offset + subscriptions.length, total);
    return `Showing ${first}–${last} of ${total}`;
  }, [offset, subscriptions.length, total]);

  const showEmpty = !loading && !error && subscriptions.length === 0 && !hasFilters;
  const showNoResults = !loading && !error && subscriptions.length === 0 && hasFilters;

  return (
    <div className="space-y-5">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">Subscriptions</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Each company’s current subscription and its status.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setReloadToken((t) => t + 1)}
          disabled={loading}
          aria-label="Refresh subscriptions"
          className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
        >
          <FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" spin={loading} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* ── Filters — search and status only (no Plan filter, D-109 §4) ────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div className="relative min-w-0">
          <FontAwesomeIcon
            icon={faSearch}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            aria-label="Search subscriptions by company name or slug"
            placeholder="Search by company name or slug…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
          />
        </div>

        <select
          aria-label="Filter by subscription status"
          value={status}
          onChange={(e) => updateParams({ status: e.target.value || null })}
          className="h-11 min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 text-destructive" />
          <p className="min-w-0 flex-1 text-sm text-destructive">{error}</p>
          <button
            type="button"
            onClick={() => setReloadToken((t) => t + 1)}
            className="rounded-lg border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            Try again
          </button>
        </div>
      )}

      {/* ── Desktop table ──────────────────────────────────────────────────── */}
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <caption className="sr-only">
              Current subscriptions, newest first. {rangeLabel}.
            </caption>
            <thead className="bg-muted/30">
              <tr>
                {['Company', 'Plan', 'Status', 'Period', 'Started', 'Expires'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {heading}
                  </th>
                ))}
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <SkeletonRows />
              ) : showEmpty || showNoResults || error ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <FontAwesomeIcon icon={faFileContract} className="mb-2 h-6 w-6 opacity-30" />
                    <p className="text-sm text-muted-foreground">
                      {error
                        ? 'Subscriptions could not be loaded.'
                        : showEmpty
                          ? 'No subscriptions yet.'
                          : 'No subscriptions match your search or filters.'}
                    </p>
                    {showNoResults && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-3 text-sm font-medium text-primary hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub) => (
                  <tr key={sub.subscriptionId} className="transition-colors hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{sub.company.name}</p>
                      <p className="text-xs text-muted-foreground">{sub.company.slug}</p>
                    </td>
                    {/* Neutral plan identity only — never a tier name or price. */}
                    <td className="px-4 py-3 text-muted-foreground">{sub.plan.slug}</td>
                    <td className="px-4 py-3">
                      <SubscriptionStatusBadge status={sub.status} />
                    </td>
                    {/* Both of these render "—" when unset, and NEVER compute. */}
                    <td className="px-4 py-3 text-muted-foreground">{formatPeriod(sub.period)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {formatSubscriptionDate(sub.startedAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {formatExpiry(sub.expiresAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        disabled={!sub.subscriptionId}
                        onClick={() => openSubscription(sub.subscriptionId)}
                        aria-label={`View subscription for ${sub.company.name}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Narrow cards ───────────────────────────────────────────────────── */}
      {/* A seven-column table cannot survive 375px, so below md the same rows
          become a semantic list of cards, with labels since headers are gone. */}
      <ul className="grid list-none grid-cols-1 gap-3 md:hidden">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="animate-pulse rounded-xl border border-border bg-card p-4">
              <div className="h-3 w-2/3 rounded bg-muted" />
              <div className="mt-3 h-3 w-1/3 rounded bg-muted" />
            </li>
          ))
        ) : showEmpty || showNoResults || error ? (
          <li className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {error
                ? 'Subscriptions could not be loaded.'
                : showEmpty
                  ? 'No subscriptions yet.'
                  : 'No subscriptions match your search or filters.'}
            </p>
            {showNoResults && (
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 text-sm font-medium text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </li>
        ) : (
          subscriptions.map((sub) => (
            <li key={sub.subscriptionId}>
              <button
                type="button"
                onClick={() => openSubscription(sub.subscriptionId)}
                aria-label={`View subscription for ${sub.company.name}`}
                className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/20"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{sub.company.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{sub.company.slug}</p>
                  </div>
                  <SubscriptionStatusBadge status={sub.status} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  <div className="flex gap-1.5">
                    <dt className="text-muted-foreground">Plan:</dt>
                    <dd className="text-foreground">{sub.plan.slug}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="text-muted-foreground">Period:</dt>
                    <dd className="text-foreground">{formatPeriod(sub.period)}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="text-muted-foreground">Started:</dt>
                    <dd className="text-foreground">{formatSubscriptionDate(sub.startedAt)}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="text-muted-foreground">Expires:</dt>
                    <dd className="text-foreground">{formatExpiry(sub.expiresAt)}</dd>
                  </div>
                </dl>
              </button>
            </li>
          ))
        )}
      </ul>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      <nav
        aria-label="Subscriptions pagination"
        className="flex flex-wrap items-center justify-between gap-3"
      >
        <p className="text-xs text-muted-foreground">{loading ? 'Loading…' : rangeLabel}</p>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Per page</span>
            <select
              aria-label="Subscriptions per page"
              value={limit}
              onChange={(e) => updateParams({ limit: Number(e.target.value) })}
              className="h-9 rounded-lg border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() =>
              updateParams({ offset: Math.max(0, offset - limit) || null }, { resetOffset: false })
            }
            disabled={loading || offset === 0}
            className="min-h-11 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => updateParams({ offset: offset + limit }, { resetOffset: false })}
            disabled={loading || offset + subscriptions.length >= total}
            className="min-h-11 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
          >
            Next
          </button>
        </div>
      </nav>
    </div>
  );
}
